import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { keys } from '@/api/keys'
import { patchSettings, settingsQuery } from '@/api/settings'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from '@/components/ui/toast'
import type { AiEffort, Settings } from '@/schema'

/**
 * The money and concurrency knobs (§5.10 `PATCH /api/settings`).
 *
 * Only the runtime-changeable keys are here — the model aliases, the
 * per-call ceilings, the daily cap and how many CLI processes may run at
 * once. Everything else on this page is read-only status. The form edits a
 * local copy and sends one patch, so a half-typed number never reaches the
 * server.
 */

const KINDS = [
  { key: 'vision' as const, label: 'Looking at pictures', hint: 'Annotating stages, characters and objects.' },
  { key: 'story' as const, label: 'Writing stories', hint: 'Outline, scenes and repairs.' },
  { key: 'cheap' as const, label: 'Small checks', hint: 'The safety judge and the health ping.' },
]

const EFFORTS: AiEffort[] = ['low', 'medium', 'high']

interface Draft {
  daily_budget_usd: string
  concurrency: string
  story_fanout: string
  models: Record<'vision' | 'story' | 'cheap', string>
  efforts: Record<'vision' | 'story' | 'cheap', AiEffort>
  budgets: Record<'vision' | 'story' | 'cheap', string>
}

function toDraft(s: Settings): Draft {
  return {
    daily_budget_usd: String(s.ai.daily_budget_usd ?? 0),
    concurrency: String(s.ai.concurrency ?? 1),
    story_fanout: String(s.ai.story_fanout ?? 1),
    models: { ...s.ai.models },
    efforts: { ...s.ai.efforts },
    budgets: {
      vision: String(s.ai.budgets.vision),
      story: String(s.ai.budgets.story),
      cheap: String(s.ai.budgets.cheap),
    },
  }
}

function num(value: string): number | null {
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? n : null
}

export function BudgetsForm() {
  const qc = useQueryClient()
  const settings = useQuery(settingsQuery)
  const [draft, setDraft] = useState<Draft | null>(null)

  useEffect(() => {
    if (settings.data) setDraft(toDraft(settings.data))
  }, [settings.data])

  const save = useMutation({
    mutationFn: (d: Draft) =>
      patchSettings({
        ai: {
          daily_budget_usd: num(d.daily_budget_usd) ?? 0,
          concurrency: Math.max(1, Math.round(num(d.concurrency) ?? 1)),
          story_fanout: Math.max(1, Math.round(num(d.story_fanout) ?? 1)),
          models: d.models,
          efforts: d.efforts,
          budgets: {
            vision: num(d.budgets.vision) ?? 0,
            story: num(d.budgets.story) ?? 0,
            cheap: num(d.budgets.cheap) ?? 0,
          },
        },
      }),
    onSuccess: (next) => {
      qc.setQueryData(keys.settings(), next)
      setDraft(toDraft(next))
      toast.success('Saved')
    },
    onError: (e) => toast.error('Could not save', { description: e instanceof Error ? e.message : String(e) }),
  })

  if (settings.isPending || !draft) {
    return (
      <section className="card p-5">
        <p className="text-sm text-ink-2">Loading settings…</p>
      </section>
    )
  }
  if (settings.isError) {
    return (
      <section className="card p-5" role="alert">
        <p className="text-sm text-ink-2">Could not load settings. {settings.error instanceof Error ? settings.error.message : ''}</p>
      </section>
    )
  }

  const s = settings.data
  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d))
  const dailyInvalid = num(draft.daily_budget_usd) === null

  return (
    <section className="card p-5" aria-labelledby="budgets-heading">
      <h2 id="budgets-heading" className="text-lg font-semibold text-ink">
        Spending and speed
      </h2>
      <p className="mt-1 text-sm text-ink-2">
        {s.ai.mode === 'mock'
          ? 'Mock mode is on, so nothing here costs anything. The numbers still apply the moment you switch to the real CLI.'
          : 'Ceilings the studio will not go past on its own.'}
      </p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field
          label="Stop for the day at (US$)"
          hint={`0 means no daily cap. ${s.ai.daily_budget_default_reason ? `Default set because: ${s.ai.daily_budget_default_reason}.` : ''}`}
          error={dailyInvalid ? 'Enter a number, or 0 for no cap.' : undefined}
        >
          <Input
            inputMode="decimal"
            value={draft.daily_budget_usd}
            onChange={(e) => set({ daily_budget_usd: e.target.value })}
            aria-invalid={dailyInvalid || undefined}
            aria-label="Daily budget in US dollars"
          />
        </Field>

        <Field label="Claude processes at once" hint="More means faster bulk runs and more memory. 1–3 on a laptop.">
          <Input type="number" min={1} max={8} value={draft.concurrency} onChange={(e) => set({ concurrency: e.target.value })} aria-label="Concurrency" />
        </Field>

        <Field label="Scenes written at once" hint="Per story. Higher finishes sooner but competes with bulk annotation.">
          <Input type="number" min={1} max={8} value={draft.story_fanout} onChange={(e) => set({ story_fanout: e.target.value })} aria-label="Story fan-out" />
        </Field>
      </div>

      <h3 className="mt-6 text-sm font-semibold text-ink">Per kind of call</h3>
      <div className="mt-2 flex flex-col gap-4">
        {KINDS.map((k) => (
          <div key={k.key} className="grid gap-3 rounded-xl border border-line p-3 sm:grid-cols-[1fr_1fr_1fr]">
            <div className="sm:col-span-3">
              <p className="text-sm font-medium text-ink">{k.label}</p>
              <p className="text-xs text-ink-3">{k.hint}</p>
            </div>
            <Field label="Model">
              <Input
                value={draft.models[k.key]}
                onChange={(e) => set({ models: { ...draft.models, [k.key]: e.target.value } })}
                aria-label={`${k.label} model`}
                placeholder="sonnet"
              />
            </Field>
            <Field label="Effort">
              <Select value={draft.efforts[k.key]} onValueChange={(v) => set({ efforts: { ...draft.efforts, [k.key]: v as AiEffort } })}>
                <SelectTrigger aria-label={`${k.label} effort`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EFFORTS.map((e) => (
                    <SelectItem key={e} value={e}>
                      {e}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Stop one call at (US$)">
              <Input
                inputMode="decimal"
                value={draft.budgets[k.key]}
                onChange={(e) => set({ budgets: { ...draft.budgets, [k.key]: e.target.value } })}
                aria-label={`${k.label} budget`}
              />
            </Field>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-2">
        <Button variant="primary" onClick={() => draft && save.mutate(draft)} loading={save.isPending} disabled={dailyInvalid}>
          Save
        </Button>
        <Button variant="ghost" onClick={() => settings.data && setDraft(toDraft(settings.data))} disabled={save.isPending}>
          Undo changes
        </Button>
      </div>
    </section>
  )
}
